import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../../shared";
import type { ChildSchoolFees, DayAttendanceType, ParentChild } from "../../../types/parentFamily";
import {
  formatCurrency,
  getChildFullName,
} from "../../../utils/parentFamilyHelpers";
import { currentAcademicYear } from "../../../utils/schoolFeesHelpers";
import { FAMILY_OUTLINED_FIELD_SX } from "./familyStyles";

export interface SchoolFeeSetupValues {
  childId?: string;
  academicYear: string;
  totalAnnualFee: string;
  paymentPlanMonths: string;
  dayType: DayAttendanceType | "";
}

interface SchoolFeeSetupModalProps {
  open: boolean;
  onClose: () => void;
  initialAccount?: ChildSchoolFees | null;
  childrenOptions?: Array<Pick<ParentChild, "id" | "firstName" | "lastName">>;
  selectedChildId?: string | null;
  onChildChange?: (childId: string) => void;
  onSave: (values: SchoolFeeSetupValues) => void | Promise<void>;
}

const PAYMENT_PLAN_OPTIONS = [10, 11, 12];

const emptyForm = (childId?: string): SchoolFeeSetupValues => ({
  childId,
  academicYear: currentAcademicYear(),
  totalAnnualFee: "",
  paymentPlanMonths: "12",
  dayType: "",
});

export const SchoolFeeSetupModal: React.FC<SchoolFeeSetupModalProps> = ({
  open,
  onClose,
  initialAccount,
  childrenOptions = [],
  selectedChildId,
  onChildChange,
  onSave,
}) => {
  const isEdit = Boolean(initialAccount);
  const canPickChild = childrenOptions.length > 0 && !isEdit;
  const [form, setForm] = useState<SchoolFeeSetupValues>(
    emptyForm(selectedChildId || undefined)
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialAccount) {
      setForm({
        childId: selectedChildId || undefined,
        academicYear: initialAccount.academicYear,
        totalAnnualFee: String(initialAccount.totalAnnualFee),
        paymentPlanMonths: String(initialAccount.paymentPlanMonths),
        dayType: initialAccount.dayType || "",
      });
    } else {
      setForm(emptyForm(selectedChildId || undefined));
    }
    setError(null);
    setSaving(false);
  }, [open, initialAccount, selectedChildId]);

  const previewInstallment = useMemo(() => {
    const total = Number.parseFloat(form.totalAnnualFee.replace(/,/g, ""));
    const months = Number.parseInt(form.paymentPlanMonths, 10);
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(months) || months <= 0) {
      return null;
    }
    return total / months;
  }, [form.totalAnnualFee, form.paymentPlanMonths]);

  const handleSubmit = async () => {
    const childId = form.childId || selectedChildId;
    if (canPickChild && !childId) {
      setError("Select which child these fees are for.");
      return;
    }
    if (!childId) {
      setError("Could not determine which child these fees are for.");
      return;
    }
    const total = Number.parseFloat(form.totalAnnualFee.replace(/,/g, ""));
    const months = Number.parseInt(form.paymentPlanMonths, 10);
    if (!form.academicYear.trim()) {
      setError("Enter the academic year.");
      return;
    }
    if (!Number.isFinite(total) || total <= 0) {
      setError("Enter a valid annual fee amount.");
      return;
    }
    if (!Number.isFinite(months) || months < 1 || months > 12) {
      setError("Payment plan must be between 1 and 12 months.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...form,
        childId,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save school fees.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit school fee setup" : "Set up school fees"}
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
          <NoticeboardButton variant="secondary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={onClose}>
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            loading={saving}
            disabled={saving}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isEdit ? "Save changes" : "Set up fees"}
          </NoticeboardButton>
        </Box>
      }
    >
      <Stack spacing={2} sx={FAMILY_OUTLINED_FIELD_SX}>
        {canPickChild ? (
          <FormControl fullWidth size="small">
            <InputLabel id="fee-child-label">Child</InputLabel>
            <Select
              labelId="fee-child-label"
              label="Child"
              value={form.childId || selectedChildId || ""}
              onChange={(e) => {
                const childId = String(e.target.value);
                setForm((f) => ({ ...f, childId }));
                onChildChange?.(childId);
              }}
            >
              {childrenOptions.map((child) => (
                <MenuItem key={child.id} value={child.id}>
                  {getChildFullName(child)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <TextField
          label="Academic year"
          fullWidth
          size="small"
          value={form.academicYear}
          onChange={(e) => setForm((f) => ({ ...f, academicYear: e.target.value }))}
          placeholder={currentAcademicYear()}
        />

        <TextField
          label="Total annual fee (ZAR)"
          fullWidth
          size="small"
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
          value={form.totalAnnualFee}
          onChange={(e) => setForm((f) => ({ ...f, totalAnnualFee: e.target.value }))}
        />

        <FormControl fullWidth size="small">
          <InputLabel id="payment-plan-label">Payment plan</InputLabel>
          <Select
            labelId="payment-plan-label"
            label="Payment plan"
            value={form.paymentPlanMonths}
            onChange={(e) =>
              setForm((f) => ({ ...f, paymentPlanMonths: String(e.target.value) }))
            }
          >
            {PAYMENT_PLAN_OPTIONS.map((months) => (
              <MenuItem key={months} value={String(months)}>
                {months} months
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="day-type-label">Full day / Half day</InputLabel>
          <Select
            labelId="day-type-label"
            label="Full day / Half day"
            value={form.dayType}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                dayType: e.target.value as DayAttendanceType | "",
              }))
            }
          >
            <MenuItem value="">Not set</MenuItem>
            <MenuItem value="full-day">Full day</MenuItem>
            <MenuItem value="half-day">Half day</MenuItem>
          </Select>
        </FormControl>

        {previewInstallment != null ? (
          <Typography variant="body2" color="text.secondary">
            Monthly installment: {formatCurrency(previewInstallment, "ZAR")}
          </Typography>
        ) : null}

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}
      </Stack>
    </ActionModal>
  );
};

export default SchoolFeeSetupModal;
